import { memo, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { AgentField, AgentShortData } from '@shared/api';

import styles from './agent-test-chat.module.scss';
import { useAgentTestChat } from './hooks/use-agent-test-chat';
import { AgentTestHeader } from './ui/agent-test-header';
import { AgentTestMessages } from './ui/agent-test-messages';
import { AgentTestRecommendations } from './ui/agent-test-recommendations';
import { AgentTestInput } from './ui/agent-test-input';

export interface AgentTestChatProps {
  agentId: number;
  agentName?: string | null;
  agentFields?: AgentField[] | null;
  availableAgents?: AgentShortData[] | null;
}

const AgentTestChatComponent: FC<AgentTestChatProps> = ({
  agentId,
  agentName,
  agentFields,
  availableAgents,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'prompt.testChat' });
  const normalizedAgentName = agentName?.trim() ?? '';
  const resolvedAgentName = normalizedAgentName || t('agentLabel');
  const {
    inputValue,
    setInputValue,
    messages,
    sendScrollSignal,
    recommendations,
    handleSendMessage,
    handleSendRecommendation,
    handleClearHistory,
    isSending,
    isRecommendationsLocked,
    isClearing,
    isLoading,
    isFetching,
    leadFields,
    clientFields,
    isLeadCreated,
    switchAgentNamesById,
  } = useAgentTestChat({ agentId, agentFields });
  const isClearDisabled = messages.length === 0 && !isLoading;
  const agentNamesById = useMemo<Record<number, string>>(() => {
    const next: Record<number, string> = {};

    (availableAgents ?? []).forEach((agent) => {
      if (typeof agent.id !== 'number') return;
      if (!agent.name?.trim()) return;

      next[agent.id] = agent.name.trim();
    });

    if (normalizedAgentName) {
      next[agentId] = normalizedAgentName;
    } else if (resolvedAgentName && !next[agentId]) {
      next[agentId] = resolvedAgentName;
    }

    return {
      ...next,
      ...switchAgentNamesById,
    };
  }, [agentId, availableAgents, normalizedAgentName, resolvedAgentName, switchAgentNamesById]);

  return (
    <div className={styles.root}>
      <AgentTestHeader
        title={resolvedAgentName}
        onClear={handleClearHistory}
        isClearing={isClearing}
        isClearDisabled={isClearDisabled}
        clearLabel={t('clearHistory')}
        leadFields={leadFields}
        clientFields={clientFields}
        isLeadCreated={isLeadCreated}
        leadTitle={t('leadDataTitle')}
        clientTitle={t('clientDataTitle')}
        leadCreatedLabel={t('leadCreated')}
        leadNotCreatedLabel={t('leadNotCreated')}
        notFilledLabel={t('notFilled')}
        infoLabel={t('info')}
      />

      <AgentTestMessages
        messages={messages}
        forceScrollSignal={sendScrollSignal}
        isLoading={isLoading}
        isFetching={isFetching}
        emptyTitle={t('title')}
        emptyDescription={t('description')}
        agentLabel={t('agentLabel')}
        agentNamesById={agentNamesById}
        switchNoticePrefix={t('switchNoticePrefix')}
        switchNoticeTooltip={t('switchNoticeTooltip')}
      />

      <AgentTestRecommendations
        items={recommendations}
        onSelect={handleSendRecommendation}
        disabled={isRecommendationsLocked}
      />

      <AgentTestInput
        value={inputValue}
        placeholder={t('inputPlaceholder')}
        isSending={isSending}
        onChange={setInputValue}
        onSend={() => handleSendMessage(inputValue)}
      />
    </div>
  );
};

export const AgentTestChat = memo(AgentTestChatComponent);
AgentTestChat.displayName = 'AgentTestChat';
